'use client';

import { useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import {
  IconPointer,
  IconSend,
  IconBackspaceFilled
} from '@tabler/icons-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

import {
  useScanContainerMutation,
  useSortingScanPageMutation
} from '@/features/sorting';

import { dispatchHidScan, isTextLikeInput } from '../lib/hid-emulator';
import type { BarcodeApiTarget, BarcodeLogEntry, BarcodeMode } from '../types';

const LOG_LIMIT = 20;

function describeTarget(el: HTMLInputElement | HTMLTextAreaElement): string {
  const id = el.id ? `#${el.id}` : '';
  const name = el.getAttribute('name');
  const placeholder = el.getAttribute('placeholder');
  return [
    el.tagName.toLowerCase(),
    id,
    name && `[name=${name}]`,
    placeholder && `"${placeholder}"`
  ]
    .filter(Boolean)
    .join(' ');
}

export function BarcodeSimulator() {
  const t = useTranslations('devTools.barcode');
  const [mode, setMode] = useState<BarcodeMode>('hid');
  const [value, setValue] = useState('');
  const [log, setLog] = useState<BarcodeLogEntry[]>([]);

  // HID mode
  const [trailingEnter, setTrailingEnter] = useState(true);
  const targetRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [targetDesc, setTargetDesc] = useState<string | null>(null);

  // API mode
  const [apiTarget, setApiTarget] =
    useState<BarcodeApiTarget>('sorting-container');
  const [sessionId, setSessionId] = useState<string>('');

  const containerScanMutation = useScanContainerMutation(
    Number(sessionId) || 0
  );
  const pageScanMutation = useSortingScanPageMutation(Number(sessionId) || 0);

  const appendLog = (entry: BarcodeLogEntry) =>
    setLog((prev) => [entry, ...prev].slice(0, LOG_LIMIT));

  const handleLockTarget = () => {
    const active = document.activeElement;
    if (!isTextLikeInput(active)) {
      toast.error(t('hid.noTargetToast'));
      return;
    }
    targetRef.current = active;
    setTargetDesc(describeTarget(active));
  };

  const handleClearTarget = () => {
    targetRef.current = null;
    setTargetDesc(null);
  };

  const handleHidSend = async () => {
    if (!value) {
      toast.error(t('hid.noValueToast'));
      return;
    }
    const target = targetRef.current;
    if (!target) {
      toast.error(t('hid.noTargetToast'));
      return;
    }
    if (!document.body.contains(target)) {
      toast.error(t('hid.targetMissingToast'));
      handleClearTarget();
      return;
    }

    await dispatchHidScan(target, value, { trailingEnter });
    appendLog({
      id: `${Date.now()}`,
      mode: 'hid',
      value,
      target: targetDesc ?? '',
      ok: true,
      message: 'sent',
      createdAt: new Date().toISOString()
    });
    toast.success(t('hid.successToast', { chars: value.length }));
  };

  const handleApiSend = async () => {
    if (!value) {
      toast.error(t('api.noValueToast'));
      return;
    }
    const sid = Number(sessionId);
    if (!sid) {
      toast.error(t('api.noSessionToast'));
      return;
    }

    try {
      if (apiTarget === 'sorting-container') {
        await containerScanMutation.mutateAsync({ container_number: value });
      } else {
        await pageScanMutation.mutateAsync({ page_qr: value });
      }
      appendLog({
        id: `${Date.now()}`,
        mode: 'api',
        value,
        target: apiTarget,
        ok: true,
        message: 'OK',
        createdAt: new Date().toISOString()
      });
      toast.success(t('api.successToast'));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      appendLog({
        id: `${Date.now()}`,
        mode: 'api',
        value,
        target: apiTarget,
        ok: false,
        message,
        createdAt: new Date().toISOString()
      });
      toast.error(t('api.errorToast'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className='flex flex-col gap-6'>
        <div className='space-y-2'>
          <Label>{t('modeLabel')}</Label>
          <RadioGroup
            value={mode}
            onValueChange={(value) => setMode(value as BarcodeMode)}
            className='flex flex-col gap-2 sm:flex-row sm:gap-6'
          >
            <div className='flex items-center gap-2'>
              <RadioGroupItem id='dev-tools-mode-hid' value='hid' />
              <Label htmlFor='dev-tools-mode-hid' className='cursor-pointer'>
                {t('modeHid')}
              </Label>
            </div>
            <div className='flex items-center gap-2'>
              <RadioGroupItem id='dev-tools-mode-api' value='api' />
              <Label htmlFor='dev-tools-mode-api' className='cursor-pointer'>
                {t('modeApi')}
              </Label>
            </div>
          </RadioGroup>
        </div>

        <div className='space-y-2'>
          <Label htmlFor='dev-tools-barcode-value'>{t('valueLabel')}</Label>
          <Input
            id='dev-tools-barcode-value'
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('valuePlaceholder')}
          />
        </div>

        {mode === 'hid' ? (
          <div className='flex flex-col gap-4 rounded-md border p-4'>
            <p className='text-muted-foreground text-xs'>{t('hid.lockHint')}</p>
            <div className='flex flex-wrap items-center gap-2'>
              <Button variant='secondary' onClick={handleLockTarget}>
                <IconPointer className='size-4' />
                {t('hid.lockTargetButton')}
              </Button>
              {targetDesc && (
                <>
                  <Badge variant='secondary' className='font-mono text-xs'>
                    {t('hid.lockedTarget', { desc: targetDesc })}
                  </Badge>
                  <Button variant='ghost' size='sm' onClick={handleClearTarget}>
                    <IconBackspaceFilled className='size-4' />
                    {t('hid.clearTargetButton')}
                  </Button>
                </>
              )}
              {!targetDesc && (
                <span className='text-muted-foreground text-xs'>
                  {t('hid.noTarget')}
                </span>
              )}
            </div>
            <div className='flex items-center gap-2'>
              <Switch
                id='dev-tools-trailing-enter'
                checked={trailingEnter}
                onCheckedChange={setTrailingEnter}
              />
              <Label
                htmlFor='dev-tools-trailing-enter'
                className='cursor-pointer'
              >
                {t('hid.trailingEnterLabel')}
              </Label>
            </div>
            <div>
              <Button onClick={handleHidSend}>
                <IconSend className='size-4' />
                {t('hid.sendButton')}
              </Button>
            </div>
          </div>
        ) : (
          <div className='flex flex-col gap-4 rounded-md border p-4'>
            <div className='grid gap-3 md:grid-cols-2'>
              <div className='space-y-2'>
                <Label>{t('api.targetLabel')}</Label>
                <Select
                  value={apiTarget}
                  onValueChange={(value) =>
                    setApiTarget(value as BarcodeApiTarget)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='sorting-container'>
                      {t('api.sortingContainer')}
                    </SelectItem>
                    <SelectItem value='sorting-page'>
                      {t('api.sortingPage')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='dev-tools-session'>
                  {t('api.sessionIdLabel')}
                </Label>
                <Input
                  id='dev-tools-session'
                  type='number'
                  min={1}
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  placeholder={t('api.sessionIdPlaceholder')}
                />
              </div>
            </div>
            <div>
              <Button
                onClick={handleApiSend}
                disabled={
                  containerScanMutation.isPending || pageScanMutation.isPending
                }
              >
                <IconSend className='size-4' />
                {t('api.sendButton')}
              </Button>
            </div>
          </div>
        )}

        <div className='space-y-2'>
          <h4 className='text-sm font-medium'>{t('logHeader')}</h4>
          {log.length === 0 ? (
            <p className='text-muted-foreground text-sm'>{t('logEmpty')}</p>
          ) : (
            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('logColumns.mode')}</TableHead>
                    <TableHead>{t('logColumns.value')}</TableHead>
                    <TableHead>{t('logColumns.target')}</TableHead>
                    <TableHead>{t('logColumns.result')}</TableHead>
                    <TableHead>{t('logColumns.sentAt')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {log.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Badge variant='outline' className='text-xs uppercase'>
                          {entry.mode}
                        </Badge>
                      </TableCell>
                      <TableCell className='max-w-xs truncate font-mono text-xs'>
                        {entry.value}
                      </TableCell>
                      <TableCell className='text-muted-foreground max-w-xs truncate font-mono text-xs'>
                        {entry.target ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={entry.ok ? 'default' : 'destructive'}
                          className={
                            entry.ok
                              ? 'bg-emerald-500/15 text-emerald-700'
                              : undefined
                          }
                        >
                          {entry.ok ? '✓' : '✗'}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-muted-foreground text-xs'>
                        {new Date(entry.createdAt).toLocaleTimeString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
